import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  Globe, RefreshCw, Trash2, Search, Loader2, ExternalLink, AlertCircle,
} from "lucide-react";

/**
 * SiteKnowledgePanel — admin UI за управление на сайт knowledge base.
 *
 * Показва статистика, списък с обходени страници, бутон „Re-crawl",
 * бутон „Изчисти" и тестова search кутия.
 */
export default function SiteKnowledgePanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [stats, setStats] = useState({ total_chunks: 0, pages_count: 0, pages: [] });
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const authHeaders = () => {
    const token =
      localStorage.getItem("nutramilo_access_token") ||
      sessionStorage.getItem("nutramilo_access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/site-knowledge/stats`, {
        headers: authHeaders(),
      });
      setStats(data);
    } catch (e) {
      toast.error(bg ? "Грешка при зареждане" : "Failed to load");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, bg]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleCrawl = async () => {
    if (!window.confirm(bg
      ? "Стартиране на пълно обхождане? Може да отнеме до 60 секунди."
      : "Start a full crawl? This can take up to 60 seconds.")) return;
    setCrawling(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/site-knowledge/crawl`,
        {},
        { headers: { ...authHeaders(), "Content-Type": "application/json" }, timeout: 120000 }
      );
      setLastResult(data);
      toast.success(
        bg
          ? `Обходени ${data.urls_ok}/${data.urls_total} · ${data.chunks_inserted} chunks`
          : `Crawled ${data.urls_ok}/${data.urls_total} · ${data.chunks_inserted} chunks`
      );
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Crawl грешка" : "Crawl failed"));
    } finally {
      setCrawling(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(bg
      ? "Изчистване на цялата knowledge база? Не може да бъде отменено."
      : "Wipe the entire knowledge base? This cannot be undone.")) return;
    setClearing(true);
    try {
      const { data } = await axios.delete(`${API}/admin/site-knowledge`, {
        headers: authHeaders(),
      });
      toast.success(bg ? `Изтрити ${data.deleted}` : `Deleted ${data.deleted}`);
      fetchStats();
    } catch {
      toast.error(bg ? "Грешка" : "Failed");
    } finally {
      setClearing(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const q = searchQ.trim();
    if (!q) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const { data } = await axios.get(
        `${API}/site-knowledge/search?q=${encodeURIComponent(q)}&limit=5`,
        { headers: authHeaders() }
      );
      setSearchResults(data.hits || []);
    } catch {
      toast.error(bg ? "Грешка при търсене" : "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const cardCls = `rounded-xl border p-4 ${
    isDark ? "bg-[hsl(222,47%,10%)] border-white/5" : "bg-white border-gray-200"
  }`;

  return (
    <div className="space-y-4" data-testid="site-knowledge-panel">
      <div className={cardCls}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Globe className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
              <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Site Knowledge Base" : "Site Knowledge Base"}
              </h3>
            </div>
            <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg
                ? "Обхожда публичните страници на nutramilo.com и индексира ги за NavigatorBot."
                : "Crawls public nutramilo.com pages and indexes them for NavigatorBot."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCrawl}
              disabled={crawling}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold
                bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95 disabled:opacity-60"
              data-testid="site-kb-crawl-btn"
            >
              {crawling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {bg ? "Re-crawl" : "Re-crawl"}
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={clearing || stats.total_chunks === 0}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold disabled:opacity-50
                ${isDark ? "bg-rose-500/15 text-rose-300 hover:bg-rose-500/25" : "bg-rose-50 text-rose-700 hover:bg-rose-100"}`}
              data-testid="site-kb-clear-btn"
            >
              {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {bg ? "Изчисти" : "Clear"}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className={`rounded-lg p-3 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <div className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {bg ? "Страници" : "Pages"}
            </div>
            <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
              data-testid="site-kb-pages-count">
              {stats.pages_count}
            </div>
          </div>
          <div className={`rounded-lg p-3 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <div className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {bg ? "Общо chunks" : "Total chunks"}
            </div>
            <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
              data-testid="site-kb-chunks-count">
              {stats.total_chunks}
            </div>
          </div>
        </div>

        {/* Last crawl summary */}
        {lastResult && (
          <div className={`mt-3 rounded-lg p-2.5 text-[11px] ${
            isDark ? "bg-emerald-500/10 text-emerald-200" : "bg-emerald-50 text-emerald-800"
          }`}>
            <strong>{bg ? "Последен crawl:" : "Last crawl:"}</strong>{" "}
            {lastResult.urls_ok}/{lastResult.urls_total} ОК · {lastResult.chunks_inserted} chunks
            {lastResult.failed?.length > 0 && (
              <div className="mt-1 flex items-start gap-1 text-amber-700 dark:text-amber-300">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>
                  {bg ? "Неуспешни:" : "Failed:"} {lastResult.failed.map((f) => f.url).join(", ")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pages list */}
      <div className={cardCls}>
        <h4 className={`text-sm font-semibold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {bg ? "Обходени страници" : "Crawled pages"}
        </h4>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : stats.pages.length === 0 ? (
          <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg ? "Няма индексирани страници. Натиснете бутона Re-crawl по-горе." : "No pages indexed. Click \"Re-crawl\" above."}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="site-kb-pages-list">
            {stats.pages.map((p) => (
              <li
                key={p.url}
                className={`flex items-center justify-between gap-2 text-xs p-2 rounded-lg
                  ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
              >
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-1.5 min-w-0 ${
                    isDark ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-800"
                  }`}
                >
                  <span className="truncate">{p.url}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
                <span className={`text-[10px] flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {p.chunks} chunks
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Test search */}
      <div className={cardCls}>
        <h4 className={`text-sm font-semibold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {bg ? "Тестово търсене" : "Test search"}
        </h4>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={bg ? "Напиши заявка..." : "Type a query..."}
            className={`flex-1 h-9 px-3 rounded-lg text-sm border focus:outline-none
              ${isDark
                ? "bg-white/5 border-white/10 text-white placeholder-gray-500"
                : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
            data-testid="site-kb-search-input"
          />
          <button
            type="submit"
            disabled={searching || !searchQ.trim()}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold
              bg-violet-600 text-white hover:opacity-95 disabled:opacity-50"
            data-testid="site-kb-search-btn"
          >
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {bg ? "Търси" : "Search"}
          </button>
        </form>
        {searchResults.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="site-kb-search-results">
            {searchResults.map((hit, i) => (
              <li
                key={i}
                className={`p-2.5 rounded-lg text-xs ${
                  isDark ? "bg-white/5 border border-white/10" : "bg-gray-50 border border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <a
                    href={hit.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`font-semibold truncate ${
                      isDark ? "text-violet-300" : "text-violet-700"
                    }`}
                  >
                    {hit.title || hit.page_path}
                  </a>
                  <span className={`text-[10px] flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    score {hit.score?.toFixed(2)}
                  </span>
                </div>
                <p className={`text-[11px] leading-relaxed line-clamp-3 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}>
                  {hit.content}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
